import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Decimal } from 'decimal.js';

import { IState } from 'models/reducers/i-state.interface';
import { IWalletCurrency } from 'models/wallet-currency-response.interface';

import { PriceCurrencySymbols } from 'enums/price-currency-symbols.enum';

import { DEFAULTS } from 'constants/defaults.constant';

import { getIconByCurrencySymbol } from 'utils/get-currency-icon-by-symbol';
import { parseDecimal } from 'utils/parser.utils';

import Web3Singleton from '../web3Singleton';

// without balance - for all users

export const useCurrency = (currencyId?: number): Omit<IWalletCurrency, 'balance'> | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.id === currencyId);

  return useMemo(
    () => currency && { ...currency, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency]
  );
};

export const useCurrencyBySymbol = (currencySymbol?: string): Omit<IWalletCurrency, 'balance'> | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.symbol === currencySymbol);

  return useMemo(
    () => currency && { ...currency, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency]
  );
};

export const useRMVCurrency = (): IWalletCurrency | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.symbol === PriceCurrencySymbols.RMV);

  return useMemo(
    () => currency && { ...currency, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency]
  );
};

export const useUSDCCurrency = (): Omit<IWalletCurrency, 'balance'> | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.symbol === PriceCurrencySymbols.USDC);

  return useMemo(
    () => currency && { ...currency, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency]
  );
};

export const useDefaultCurrency = (): Omit<IWalletCurrency, 'balance'> | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.symbol === DEFAULTS.defaultCurrencySymbol);

  return useMemo(
    () => currency && { ...currency, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency]
  );
};

// with balance - for authorized users

export const useCurrencyWithBalance = (currencyId?: number): Required<IWalletCurrency> | undefined => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.id === currencyId);
  const [balance, setBalance] = useState('0');
  const lgoWeb3 = Web3Singleton.getInstance();

  useEffect(() => {
    if (!currencies?.length || !currency || !walletAddress) {
      setBalance('0');

      return;
    }

    const fetchCurrenciesBalance = async () => {
      const balance = await lgoWeb3.getCurrencyBalance(currency.address, walletAddress);

      setBalance(balance);
    };

    fetchCurrenciesBalance();
  }, [currency, currencies, walletAddress, lgoWeb3, setBalance]);

  return useMemo(
    () => currency && { ...currency, balance, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency, balance]
  );
};

export const useRMVCurrencyWithBalance = (): Required<IWalletCurrency> | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.symbol === PriceCurrencySymbols.RMV);
  const balance = useCurrencyBalance(currency?.id);

  return useMemo(
    () => currency && { ...currency, balance, icon: getIconByCurrencySymbol(currency.symbol) },
    [currency, balance]
  );
};

export const useUSDCCurrencyWithBalance = (): Required<IWalletCurrency> | undefined => {
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.symbol === PriceCurrencySymbols.USDC);

  return useCurrencyWithBalance(currency?.id);
};

// parsed currency balance

export const useCurrencyBalance = (currencyId?: number): string => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const currencies = useSelector((state: IState) => state.wallet.currencies);
  const currency = currencies?.find((currency) => currency.id === currencyId);
  const [balance, setBalance] = useState('0');
  const lgoWeb3 = Web3Singleton.getInstance();

  useEffect(() => {
    if (!currencies?.length || !currency || !walletAddress) {
      setBalance('0');

      return;
    }

    const fetchCurrenciesBalance = async () => {
      const balance = await lgoWeb3.getCurrencyBalance(currency.address, walletAddress);

      setBalance(balance);
    };

    fetchCurrenciesBalance();
  }, [currency, currencies, walletAddress, lgoWeb3, setBalance]);

  return currency && balance ? parseDecimal(balance, currency.decimal) : '0';
};

export const useRMVCurrencyBalance = (): string => {
  const rmvCurrency = useRMVCurrency();

  return useCurrencyBalance(rmvCurrency?.id);
};

export const useRMVCurrencyBalanceForWallets = (selectedWallets: string[] = []): { balance: string; loading: boolean } => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);
  const rmvCurrency = useRMVCurrency();
  const [balance, setBalance] = useState('0');
  const [loading, setLoading] = useState(false);
  const lgoWeb3 = Web3Singleton.getInstance();

  const allWallets = [walletAddress].filter(Boolean);
  const walletAddresses = selectedWallets.length > 0 ? selectedWallets : allWallets;
  const walletAddressesKey = walletAddresses.join(',');
  const rmvCurrencyAddress = rmvCurrency?.address;
  const rmvCurrencyDecimal = rmvCurrency?.decimal;

  useEffect(() => {
    if (!rmvCurrencyAddress || rmvCurrencyDecimal === undefined || walletAddresses.length === 0) {
      setBalance('0');
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    const fetchBalances = async () => {
      const balances = await Promise.all(
        walletAddresses.map((address) => lgoWeb3.getCurrencyBalance(rmvCurrencyAddress, address))
      );

      if (cancelled) return;

      const total = balances.reduce(
        (sum, rawBalance) => sum.plus(new Decimal(rawBalance).div(new Decimal(10).pow(rmvCurrencyDecimal))),
        new Decimal(0)
      );

      setBalance(total.toString());
      setLoading(false);
    };

    fetchBalances().catch(() => {
      if (!cancelled) {
        setBalance('0');
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [rmvCurrencyAddress, rmvCurrencyDecimal, walletAddressesKey, lgoWeb3]);

  return { balance, loading };
};

export const useUSDCCurrencyBalance = (): string => {
  const usdcCurrency = useUSDCCurrency();

  return useCurrencyBalance(usdcCurrency?.id);
};
